import React, { useCallback, useEffect, useState } from 'react';
import { Mail, AlarmClock, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';

/**
 * PendingInvitesTile
 * ------------------
 * Admin Dashboard summary tile that shows the invitation funnel at
 * a glance:
 *   • active   — temp credentials still valid (green)
 *   • expired  — TTL lapsed; the user can no longer sign in until
 *                someone re-issues the invite (rose)
 *
 * Backed by GET /api/rbac/pending-invitations/summary. The
 * "Resend all expired" button hits POST
 * /api/rbac/pending-invitations/resend-expired and refreshes the
 * tile so the rose count drops immediately.
 *
 * Visible to super admins (every org) and org admins (their org
 * only — backend enforces scope). The tile silently no-ops if the
 * caller lacks permission so it can be dropped into shared
 * dashboard layouts without role gating.
 */
export default function PendingInvitesTile() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get('/rbac/pending-invitations/summary');
      setSummary(res.data);
    } catch (err) {
      // Permission denied — caller isn't an admin. Render nothing.
      if (err?.response?.status === 403) {
        setSummary(null);
      } else {
        // Transient network blip — leave the previous payload up so
        // the tile doesn't flash empty on a flaky call.
        console.error('[PendingInvitesTile] refresh failed', err);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleResendAllExpired = async () => {
    if (!summary?.expired_count) return;
    if (!confirm(
      `Re-send invitations to ${summary.expired_count} expired user(s)? `
      + `Each will receive a fresh 24-hour temp password and any active sessions will be invalidated.`,
    )) {
      return;
    }
    setResending(true);
    try {
      const res = await api.post('/rbac/pending-invitations/resend-expired');
      const { resent_count, sent_count } = res.data || {};
      if (resent_count === 0) {
        toast.info('No expired invitations to resend.');
      } else if (sent_count === resent_count) {
        toast.success(`Re-sent ${resent_count} invitation(s) — all delivered.`);
      } else {
        toast.warning(
          `Re-issued ${resent_count} temp password(s); ${sent_count} email(s) delivered. `
          + `The rest fell back to copy-paste credentials in the audit log.`,
        );
      }
      await refresh();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(e => e.msg || JSON.stringify(e)).join('; ')
        : (typeof detail === 'string' ? detail : 'Bulk resend failed');
      toast.error(msg);
    } finally {
      setResending(false);
    }
  };

  if (loading || !summary) return null;
  if (summary.total === 0) return null;

  const { active_count: active, expired_count: expired, total } = summary;

  return (
    <Card data-testid="pending-invites-tile" className="border-amber-200">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Mail size={16} className="text-amber-600" />
            Pending invitations
          </CardTitle>
          <Badge variant="outline" className="text-[11px] tracking-wide">
            {total} total
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div
            className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2"
            data-testid="pending-invites-active-count"
          >
            <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">
              Active
            </div>
            <div className="text-2xl font-bold text-emerald-700 leading-tight">
              {active}
            </div>
            <div className="text-[11px] text-emerald-700/80">temp pw still valid</div>
          </div>
          <div
            className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2"
            data-testid="pending-invites-expired-count"
          >
            <div className="text-[10px] uppercase tracking-wide text-rose-700 font-semibold flex items-center gap-1">
              <AlarmClock size={10} />
              Expired
            </div>
            <div className="text-2xl font-bold text-rose-700 leading-tight">
              {expired}
            </div>
            <div className="text-[11px] text-rose-700/80">need fresh invite</div>
          </div>
        </div>

        {expired > 0 && (
          <Button
            onClick={handleResendAllExpired}
            disabled={resending}
            size="sm"
            className="w-full bg-rose-600 hover:bg-rose-700"
            data-testid="resend-all-expired-btn"
          >
            <RefreshCw
              size={14}
              className={`mr-2 ${resending ? 'animate-spin' : ''}`}
            />
            {resending ? 'Re-sending…' : `Re-send all ${expired} expired`}
          </Button>
        )}
        {expired === 0 && active > 0 && (
          <p
            className="text-xs text-slate-500 italic"
            data-testid="pending-invites-all-active-note"
          >
            Every pending invitation is still within its 24-hour window.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
